import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../contexts/NotificationContext'
import { usersApi } from '../../services/api'
import { PageLoader, Card, Badge, EmptyState, ConfirmModal } from '../../components/ui'
import SlideUpPanel from '../../components/ui/SlideUpPanel'
import { 
  Users, Plus, Search, Mail, MoreVertical, Edit, Trash2, RefreshCw, 
  Shield, ShieldOff, Key, User, Loader2, X, Copy, CheckCircle, 
  AlertTriangle, Clock, KeyRound
} from 'lucide-react'

// Helper per estrarre array utenti dalla risposta API
const extractUsers = (response) => {
  const data = response?.data
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.data?.users)) return data.data.users
  if (Array.isArray(data?.users)) return data.users
  if (Array.isArray(data?.data)) return data.data
  return []
}

export default function UsersPage() {
  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  
  // State per modale reset 2FA
  const [reset2FAModalOpen, setReset2FAModalOpen] = useState(false)
  const [reset2FAUser, setReset2FAUser] = useState(null)
  const [reset2FAReason, setReset2FAReason] = useState('')
  const [resetting2FA, setResetting2FA] = useState(false)
  
  // === NUOVI STATE PER PIN ===
  const [inviteResult, setInviteResult] = useState(null) // Risultato invito con PIN
  const [pinCopied, setPinCopied] = useState(false)
  
  // State per modal dettaglio educatore (PIN)
  const [selectedUser, setSelectedUser] = useState(null)
  const [showUserModal, setShowUserModal] = useState(false)
  const [loadingUserInfo, setLoadingUserInfo] = useState(false)
  const [userInviteInfo, setUserInviteInfo] = useState(null)
  const [resendingInvite, setResendingInvite] = useState(false)
  const [resendResult, setResendResult] = useState(null)
  
  const { user: currentUser, tenant } = useAuth()
  const { showSuccess, showError } = useNotification()

  useEffect(() => { loadUsers() }, [])

  const loadUsers = async () => {
    try {
      setLoading(true)
      const res = await usersApi.list()
      const usersData = extractUsers(res)
      setUsers(usersData)
    } catch (error) {
      console.error('Error loading users:', error)
      showError('Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  const filteredUsers = users.filter(u =>
    u.nome?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.cognome?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.username?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // ==========================================
  // INVITE HANDLERS (con supporto PIN)
  // ==========================================

  const handleInvite = async (data) => {
    try {
      const res = await usersApi.invite(data)
      
      // Se c'è un PIN nella risposta, mostralo
      if (res.data.pin) {
        setInviteResult({
          email: data.email,
          nome: data.nome,
          cognome: data.cognome,
          pin: res.data.pin,
          pinMessage: res.data.pinMessage
        })
        showSuccess('Invito inviato! Comunica il PIN all\'utente.')
      } else {
        showSuccess(`Invito inviato a ${data.email}`)
        setShowForm(false)
      }
      
      loadUsers()
    } catch (error) {
      showError(error.response?.data?.message || 'Errore nell\'invio')
    }
  }

  const handleCopyPin = () => {
    if (inviteResult?.pin) {
      navigator.clipboard.writeText(inviteResult.pin)
      setPinCopied(true)
      setTimeout(() => setPinCopied(false), 2000)
    }
  }

  const handleCloseInviteForm = () => {
    setShowForm(false)
    setEditingUser(null)
    setInviteResult(null)
  }

  // ==========================================
  // USER DETAIL MODAL (per PIN educatore)
  // ==========================================

  const handleUserClick = async (user) => {
    // Solo per utenti non attivi (pending/expired)
    if (user.attivo && user.inviteStatus !== 'pending') return
    
    setSelectedUser(user)
    setShowUserModal(true)
    setLoadingUserInfo(true)
    setUserInviteInfo(null)
    setResendResult(null)
    
    try {
      const res = await usersApi.getInviteInfo(user._id)
      setUserInviteInfo(res.data.data.user)
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nel recupero info invito')
      setShowUserModal(false)
    } finally {
      setLoadingUserInfo(false)
    }
  }

  const handleResendInvite = async () => {
    if (!selectedUser) return
    
    setResendingInvite(true)
    setResendResult(null)
    
    try {
      const res = await usersApi.resendInvite(selectedUser._id, {
        requirePin: true // Sempre con PIN per sicurezza
      })
      
      if (res.data.pin) {
        setResendResult({
          pin: res.data.pin,
          message: res.data.pinMessage
        })
        showSuccess('Nuovo invito inviato!')
      } else {
        showSuccess('Nuovo invito inviato!')
        setShowUserModal(false)
      }
      
      loadUsers()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nel reinvio')
    } finally {
      setResendingInvite(false)
    }
  }

  const handleCopyUserPin = () => {
    const pinToCopy = resendResult?.pin || userInviteInfo?.pin
    if (pinToCopy) {
      navigator.clipboard.writeText(pinToCopy)
      setPinCopied(true)
      setTimeout(() => setPinCopied(false), 2000)
    }
  }

  const handleCloseUserModal = () => {
    setShowUserModal(false)
    setSelectedUser(null)
    setUserInviteInfo(null)
    setResendResult(null)
  }

  // ==========================================
  // OTHER HANDLERS
  // ==========================================

  const handleUpdate = async (data) => {
    try {
      const { role, ...otherData } = data
      const originalRole = editingUser.role
      
      if (Object.keys(otherData).length > 0) {
        await usersApi.update(editingUser._id, otherData)
      }
      
      if (role && role !== originalRole) {
        await usersApi.changeRole(editingUser._id, role)
        showSuccess(`Ruolo cambiato a ${role === 'coordinatore' ? 'Coordinatore' : 'Educatore'}`)
      } else {
        showSuccess('Utente aggiornato')
      }
      
      setEditingUser(null)
      setShowForm(false)
      loadUsers()
    } catch (error) {
      showError(error.response?.data?.message || 'Errore')
    }
  }

  const handleDelete = async (user) => {
    try {
      await usersApi.delete(user._id)
      showSuccess(`${user.nome || user.username} rimosso`)
      setConfirmDelete(null)
      loadUsers()
    } catch (error) {
      showError(error.response?.data?.message || 'Errore')
    }
  }

  const handleQuickResendInvite = async (user) => {
    try {
      await usersApi.resendInvite(user._id)
      showSuccess(`Invito reinviato a ${user.email}`)
    } catch (error) {
      showError('Errore nel reinvio')
    }
  }

  // ==========================================
  // 2FA MANAGEMENT FUNCTIONS
  // ==========================================

  const openReset2FAModal = (user) => {
    setReset2FAUser(user)
    setReset2FAReason('')
    setReset2FAModalOpen(true)
  }

  const handleReset2FA = async () => {
    if (!reset2FAUser) return
    
    try {
      setResetting2FA(true)
      await usersApi.reset2FA(reset2FAUser._id, { reason: reset2FAReason })
      showSuccess(`Email di reset 2FA inviata a ${reset2FAUser.email}`)
      setReset2FAModalOpen(false)
      setReset2FAUser(null)
      setReset2FAReason('')
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nel reset 2FA')
    } finally {
      setResetting2FA(false)
    }
  }

  const handleForceReset2FA = async (user) => {
    if (!confirm(`Sei sicuro di voler resettare immediatamente la 2FA di ${user.nome || user.email}? Dovrà riconfigurarla al prossimo accesso.`)) {
      return
    }
    
    try {
      await usersApi.forceReset2FA(user._id, { reason: 'Reset forzato dal coordinatore' })
      showSuccess(`2FA resettata per ${user.nome || user.email}`)
      loadUsers()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nel reset 2FA')
    }
  }

  if (loading) return <PageLoader />

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Users className="h-7 w-7 mr-2 text-primary-600" />Utenti
        </h1>
        <button onClick={() => { setEditingUser(null); setInviteResult(null); setShowForm(true) }} className="btn-primary flex items-center">
          <Plus className="h-4 w-4 mr-2" />Invita Educatore
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input type="text" placeholder="Cerca utente..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="input pl-10" />
      </div>

      {/* Users List */}
      <Card>
        {filteredUsers.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {filteredUsers.map(user => (
              <UserItem 
                key={user._id} 
                user={user} 
                isCurrentUser={user._id === currentUser?.id}
                onEdit={() => { setEditingUser(user); setShowForm(true) }}
                onDelete={() => setConfirmDelete(user)}
                onResendInvite={() => handleQuickResendInvite(user)}
                onReset2FA={openReset2FAModal}
                onForceReset2FA={handleForceReset2FA}
                onUserClick={() => handleUserClick(user)}
              />
            ))}
          </div>
        ) : (
          <EmptyState icon={Users} title="Nessun utente" description={searchQuery ? 'Prova ricerca diversa' : 'Invita il primo educatore'} />
        )}
      </Card>

      {/* Info Limiti Piano */}
      <Card className="bg-gray-50 dark:bg-gray-700/50">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Utenti nel piano</p>
            <p className="text-2xl font-bold text-primary-600">
              {users.filter(u => u.role === 'educatore').length} / {tenant?.limits?.maxEducatori || '∞'}
              <span className="text-sm font-normal text-gray-500 ml-2">educatori</span>
            </p>
          </div>
          <Users className="h-12 w-12 text-gray-300" />
        </div>
      </Card>

      {/* Form Invito/Modifica */}
      <SlideUpPanel isOpen={showForm} onClose={handleCloseInviteForm} title={editingUser ? 'Modifica Utente' : 'Invita Educatore'}>
        {inviteResult ? (
          // Mostra PIN dopo invito
          <div className="p-4 space-y-4">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                <span className="font-medium text-green-800 dark:text-green-200">
                  Invito Inviato!
                </span>
              </div>
              <p className="text-sm text-green-700 dark:text-green-300">
                Email inviata a {inviteResult.email}
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  PIN di Verifica
                </span>
                <button
                  onClick={handleCopyPin}
                  className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  <Copy className="h-4 w-4" />
                  {pinCopied ? 'Copiato!' : 'Copia'}
                </button>
              </div>
              <div className="text-3xl font-mono font-bold text-center tracking-widest text-gray-900 dark:text-white py-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                {inviteResult.pin}
              </div>
            </div>
            
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <Shield className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800 dark:text-amber-200">
                  <p className="font-medium mb-1">Importante!</p>
                  <p>
                    Comunica questo PIN a <strong>{inviteResult.nome} {inviteResult.cognome}</strong> di 
                    persona, per telefono o via chat. Senza il PIN non potrà completare la registrazione.
                  </p>
                </div>
              </div>
            </div>
            
            <button
              onClick={handleCloseInviteForm}
              className="w-full btn-primary"
            >
              Ho comunicato il PIN
            </button>
          </div>
        ) : (
          <UserForm 
            user={editingUser} 
            onSave={editingUser ? handleUpdate : handleInvite} 
            onCancel={handleCloseInviteForm}
            currentUserId={currentUser?.id}
            tenantOwnerId={tenant?.ownerId}
          />
        )}
      </SlideUpPanel>

      {/* Confirm Delete */}
      <ConfirmModal isOpen={!!confirmDelete} onClose={() => setConfirmDelete(null)} onConfirm={() => handleDelete(confirmDelete)} 
        title="Rimuovi Utente" message={`Sei sicuro di voler rimuovere ${confirmDelete?.nome || confirmDelete?.username}? Non potrà più accedere.`} confirmText="Rimuovi" danger />

      {/* Modal Reset 2FA */}
      {reset2FAModalOpen && reset2FAUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <Key className="h-6 w-6 text-yellow-500 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Reset 2FA
              </h3>
            </div>
            
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Stai per inviare una richiesta di reset 2FA a{' '}
              <strong>{reset2FAUser.nome} {reset2FAUser.cognome}</strong>.
              <br />
              L'utente riceverà un'email per confermare il reset.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Motivo (opzionale)
              </label>
              <textarea
                value={reset2FAReason}
                onChange={(e) => setReset2FAReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                rows={3}
                placeholder="Es: L'utente ha perso l'accesso al telefono"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setReset2FAModalOpen(false)
                  setReset2FAUser(null)
                  setReset2FAReason('')
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Annulla
              </button>
              <button
                onClick={handleReset2FA}
                disabled={resetting2FA}
                className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 disabled:opacity-50 flex items-center"
              >
                {resetting2FA ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Invio...
                  </>
                ) : (
                  <>
                    <Key className="h-4 w-4 mr-2" />
                    Invia richiesta
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MODAL DETTAGLIO UTENTE (PIN) */}
      {/* ============================================ */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={handleCloseUserModal} />
            
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <KeyRound className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Invito Educatore
                  </h3>
                </div>
                <button
                  onClick={handleCloseUserModal}
                  className="p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {/* Loading */}
              {loadingUserInfo ? (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500 dark:text-gray-400">Caricamento...</p>
                </div>
              ) : resendResult ? (
                /* Nuovo PIN dopo reinvio */
                <div className="space-y-4">
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                      <span className="font-medium text-green-800 dark:text-green-200">
                        Nuovo Invito Inviato!
                      </span>
                    </div>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      Email inviata a {selectedUser.email}
                    </p>
                  </div>
                  
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-amber-800 dark:text-amber-200">
                        <p className="font-medium">Il PIN precedente non è più valido!</p>
                        <p className="mt-1">Comunica il NUOVO PIN all'educatore.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        <Key className="h-4 w-4" />
                        Nuovo PIN
                      </span>
                      <button
                        onClick={handleCopyUserPin}
                        className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                      >
                        <Copy className="h-4 w-4" />
                        {pinCopied ? 'Copiato!' : 'Copia'}
                      </button>
                    </div>
                    <div className="text-3xl font-mono font-bold text-center tracking-widest text-gray-900 dark:text-white py-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      {resendResult.pin}
                    </div>
                  </div>
                  
                  <button
                    onClick={handleCloseUserModal}
                    className="w-full btn-primary"
                  >
                    Ho comunicato il nuovo PIN
                  </button>
                </div>
              ) : userInviteInfo ? (
                /* Info educatore */
                <div className="space-y-4">
                  {/* Info utente */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                        <User className="h-5 w-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {userInviteInfo.nome} {userInviteInfo.cognome}
                        </p>
                        <p className="text-sm text-gray-500">{userInviteInfo.email}</p>
                      </div>
                    </div>
                    <Badge variant={userInviteInfo.role === 'coordinatore' ? 'primary' : 'default'}>
                      {userInviteInfo.role === 'coordinatore' ? 'Coordinatore' : 'Educatore'}
                    </Badge>
                  </div>
                  
                  {userInviteInfo.isExpired ? (
                    /* Invito scaduto */
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                        <span className="font-medium text-red-800 dark:text-red-200">
                          Invito Scaduto
                        </span>
                      </div>
                      <p className="text-sm text-red-700 dark:text-red-300">
                        Reinvia l'invito per generare un nuovo link e PIN.
                      </p>
                    </div>
                  ) : userInviteInfo.pin ? (
                    /* PIN disponibile */
                    <>
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          <span className="font-medium text-blue-800 dark:text-blue-200">
                            In attesa di registrazione
                          </span>
                        </div>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          Scade il {new Date(userInviteInfo.inviteExpires).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                      
                      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                            <Key className="h-4 w-4" />
                            PIN di Verifica
                          </span>
                          <button
                            onClick={handleCopyUserPin}
                            className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                          >
                            <Copy className="h-4 w-4" />
                            {pinCopied ? 'Copiato!' : 'Copia'}
                          </button>
                        </div>
                        <div className="text-3xl font-mono font-bold text-center tracking-widest text-gray-900 dark:text-white py-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          {userInviteInfo.pin}
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                        Comunica questo PIN a {userInviteInfo.nome} per completare la registrazione.
                      </p>
                    </>
                  ) : !userInviteInfo.invitePinRequired ? (
                    /* Invito senza PIN */
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <span className="font-medium text-blue-800 dark:text-blue-200">
                          In attesa di registrazione
                        </span>
                      </div>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        Invito inviato senza PIN di verifica.
                        {userInviteInfo.inviteExpires && (
                          <> Scade il {new Date(userInviteInfo.inviteExpires).toLocaleDateString('it-IT')}</>
                        )}
                      </p>
                    </div>
                  ) : (
                    /* PIN richiesto ma non disponibile */
                    <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl p-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                        PIN non disponibile. Reinvia l'invito per generarne uno nuovo.
                      </p>
                    </div>
                  )}
                  
                  {/* Pulsanti azione */}
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={handleResendInvite}
                      disabled={resendingInvite}
                      className="flex-1 btn-secondary flex items-center justify-center"
                    >
                      {resendingInvite ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Reinvia Invito
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleCloseUserModal}
                      className="flex-1 btn-primary"
                    >
                      Chiudi
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================
// USER ITEM COMPONENT
// ============================================

function UserItem({ user, isCurrentUser, onEdit, onDelete, onResendInvite, onReset2FA, onForceReset2FA, onUserClick }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const isPending = user.inviteStatus === 'pending' || user.status === 'pending' || (!user.attivo && user.inviteStatus === 'pending')

  return (
    <div 
      className={`py-4 -mx-4 lg:-mx-6 px-4 lg:px-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 ${isPending ? 'cursor-pointer' : ''}`}
      onClick={() => isPending && onUserClick()}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${user.role === 'coordinatore' ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-primary-100 dark:bg-primary-900/30'}`}>
            {user.role === 'coordinatore' ? <Shield className="h-5 w-5 text-purple-600" /> : <User className="h-5 w-5 text-primary-600" />}
          </div>
          <div className="min-w-0">
            <p className="font-medium text-gray-900 dark:text-white truncate">
              {user.nome && user.cognome ? `${user.nome} ${user.cognome}` : user.username || user.email}
              {isCurrentUser && <span className="text-xs text-gray-500 ml-2">(tu)</span>}
            </p>
            <p className="text-sm text-gray-500 truncate">{user.email}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2 flex-shrink-0 ml-3" onClick={(e) => e.stopPropagation()}>
          <div className="hidden sm:flex items-center space-x-2">
            <Badge variant={user.role === 'coordinatore' ? 'primary' : 'default'}>
              {user.role === 'coordinatore' ? 'Coordinatore' : 'Educatore'}
            </Badge>
            
            {/* Indicatore 2FA */}
            {user.twoFactorEnabled ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                <Shield className="h-3 w-3 mr-1" />
                2FA
              </span>
            ) : user.role === 'coordinatore' ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                <ShieldOff className="h-3 w-3 mr-1" />
                2FA
              </span>
            ) : null}
            
            {isPending && (
              <Badge variant="warning" className="cursor-pointer">
                <Clock className="h-3 w-3 mr-1" />
                In attesa
              </Badge>
            )}
          </div>
          
          {!isCurrentUser && (
            <div className="relative">
              <button onClick={() => setMenuOpen(!menuOpen)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600">
                <MoreVertical className="h-5 w-5" />
              </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 z-20">
                  <button onClick={() => { setMenuOpen(false); onEdit() }} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center">
                    <Edit className="h-4 w-4 mr-2" />Modifica
                  </button>
                  {isPending && (
                    <>
                      <button onClick={() => { setMenuOpen(false); onUserClick() }} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center text-blue-600">
                        <Key className="h-4 w-4 mr-2" />Vedi PIN
                      </button>
                      <button onClick={() => { setMenuOpen(false); onResendInvite() }} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center">
                        <RefreshCw className="h-4 w-4 mr-2" />Reinvia invito
                      </button>
                    </>
                  )}
                  
                  {/* Reset 2FA - solo se ha 2FA attivo */}
                  {user.twoFactorEnabled && (
                    <>
                      <button
                        onClick={() => {
                          setMenuOpen(false)
                          onReset2FA(user)
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 flex items-center"
                      >
                        <Key className="h-4 w-4 mr-2" />
                        Reset 2FA (email)
                      </button>
                      <button
                        onClick={() => {
                          setMenuOpen(false)
                          onForceReset2FA(user)
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center"
                      >
                        <ShieldOff className="h-4 w-4 mr-2" />
                        Force Reset 2FA
                      </button>
                    </>
                  )}
                  
                  <button onClick={() => { setMenuOpen(false); onDelete() }} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center text-red-600">
                    <Trash2 className="h-4 w-4 mr-2" />Rimuovi
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      </div>
      
      {/* Badges visibili solo su mobile, sotto il nome */}
      <div className="flex items-center flex-wrap gap-2 mt-2 pl-[52px] sm:hidden">
        <Badge variant={user.role === 'coordinatore' ? 'primary' : 'default'}>
          {user.role === 'coordinatore' ? 'Coordinatore' : 'Educatore'}
        </Badge>
        
        {user.twoFactorEnabled ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
            <Shield className="h-3 w-3 mr-1" />
            2FA
          </span>
        ) : user.role === 'coordinatore' ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
            <ShieldOff className="h-3 w-3 mr-1" />
            2FA
          </span>
        ) : null}
        
        {isPending && (
          <Badge variant="warning" className="cursor-pointer">
            <Clock className="h-3 w-3 mr-1" />
            In attesa
          </Badge>
        )}
      </div>
    </div>
  )
}

// ============================================
// USER FORM COMPONENT (con supporto PIN)
// ============================================

function UserForm({ user, onSave, onCancel, currentUserId, tenantOwnerId }) {
  const [formData, setFormData] = useState({
    email: user?.email || '',
    nome: user?.nome || '',
    cognome: user?.cognome || '',
    role: user?.role || 'educatore',
    requirePin: false // Default: senza PIN
  })
  const [loading, setLoading] = useState(false)

  // Determina se il cambio ruolo è disabilitato
  const isOwner = user && tenantOwnerId && user._id === tenantOwnerId
  const isSelf = user && currentUserId && user._id === currentUserId
  const roleChangeDisabled = isOwner || isSelf

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    await onSave(formData)
    setLoading(false)
  }

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
        <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
          className="input" placeholder="educatore@email.it" disabled={!!user} />
        {!user && <p className="mt-1 text-xs text-gray-500">Verrà inviato un invito a questo indirizzo</p>}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome *</label>
          <input type="text" required value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} className="input" placeholder="Mario" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cognome *</label>
          <input type="text" required value={formData.cognome} onChange={(e) => setFormData({ ...formData, cognome: e.target.value })} className="input" placeholder="Rossi" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ruolo</label>
        <select 
          value={formData.role} 
          onChange={(e) => setFormData({ ...formData, role: e.target.value })} 
          className="input"
          disabled={roleChangeDisabled}
        >
          <option value="educatore">Educatore</option>
          <option value="coordinatore">Coordinatore</option>
        </select>
        {isOwner && (
          <p className="mt-1 text-xs text-amber-600">Il proprietario del centro deve rimanere coordinatore</p>
        )}
        {isSelf && !isOwner && (
          <p className="mt-1 text-xs text-gray-500">Non puoi cambiare il tuo ruolo</p>
        )}
      </div>
      
      {/* Opzione PIN (solo per nuovo invito) */}
      {!user && (
        <div className="flex items-start gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <input
            type="checkbox"
            id="requirePin"
            checked={formData.requirePin}
            onChange={(e) => setFormData({ ...formData, requirePin: e.target.checked })}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-0.5"
          />
          <label htmlFor="requirePin" className="text-sm text-gray-700 dark:text-gray-300">
            <span className="flex items-center gap-1">
              <Shield className="h-4 w-4 text-green-600" />
              <span className="font-medium">Richiedi PIN di verifica</span>
            </span>
            <span className="text-gray-500 dark:text-gray-400 block text-xs mt-0.5">
              Dovrai comunicare un PIN a 6 cifre all'educatore per confermare la sua identità
            </span>
          </label>
        </div>
      )}
      
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button type="button" onClick={onCancel} className="btn-secondary">Annulla</button>
        <button type="submit" disabled={loading} className="btn-primary flex items-center">
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {user ? 'Salvataggio...' : 'Invio...'}
            </>
          ) : user ? (
            'Salva'
          ) : (
            <>
              <Mail className="h-4 w-4 mr-2" />
              Invia Invito
            </>
          )}
        </button>
      </div>
    </form>
  )
}
